import { type BoxProps } from '@chakra-ui/react'
import {
  Children,
  type ReactElement,
  type ReactNode,
  isValidElement,
  useMemo,
} from 'react'

import type { GridLayoutViewProps } from '../GridLayout.types'
import { GridLayoutCanvas } from '../GridLayoutCanvas'
import {
  GridLayoutCollision,
  type GridLayoutPlacedItem,
} from '../GridLayoutCollision'
import { GridLayoutItem } from '../GridLayoutItem'

const DEFAULT_COLUMNS = 12
const DEFAULT_GAP = '24px'

export type GridLayoutViewComponentProps = GridLayoutViewProps &
  Omit<BoxProps, 'children' | 'gap'> & {
    /**
     * Animate items when viewport reflow moves them.
     * @default true
     */
    animateLayout?: boolean
  }

type ExtractedItem = {
  id: string
  colSpan?: number
  minWidth?: string
  node: ReactNode
}

const isGridLayoutItemElement = (
  child: ReactElement,
): child is ReactElement<{
  colSpan?: number
  minWidth?: string
  children?: ReactNode
}> => {
  const type = child.type
  if (type === GridLayoutItem) return true
  return (
    typeof type === 'function' &&
    'displayName' in type &&
    (type as { displayName?: string }).displayName === 'GridLayoutItem'
  )
}

const extractItems = (children: ReactNode): ExtractedItem[] => {
  const entries: ExtractedItem[] = []
  Children.forEach(children, (child, index) => {
    if (child == null || child === false) return
    if (!isValidElement(child)) {
      entries.push({ id: `item-${index}`, node: child })
      return
    }
    if (isGridLayoutItemElement(child)) {
      entries.push({
        id: `item-${index}`,
        colSpan: child.props.colSpan,
        minWidth: child.props.minWidth,
        node: child.props.children,
      })
      return
    }
    entries.push({ id: `item-${index}`, node: child })
  })
  return entries
}

const toPlacedItems = (
  entries: ExtractedItem[],
  columns: number,
): GridLayoutPlacedItem[] => {
  const allImplicit = entries.every(entry => entry.colSpan == null)
  const equalSpan = allImplicit
    ? GridLayoutCollision.equalShareColSpan(entries.length, columns)
    : null

  const seeds = entries.map(entry => ({
    id: entry.id,
    minWidth: entry.minWidth,
    colSpan: equalSpan ?? Math.min(Math.max(entry.colSpan ?? 1, 1), columns),
  }))

  return GridLayoutCollision.pack(seeds, columns)
}

/**
 * Responsive CSS-grid layout for dashboard widgets (same engine as the editor).
 *
 * Default: children that omit `colSpan` share the row equally.
 * Explicit `colSpan` on {@link GridLayoutItem} is packed in reading order.
 * Per-widget `minWidth` drives display-only viewport reflow.
 *
 * Height is intrinsic (content-driven); peers share the row track — consumers
 * should fill the cell. See `requirements.mdc` (GL-R-*).
 */
export const GridLayoutView = ({
  columns = DEFAULT_COLUMNS,
  gap = DEFAULT_GAP,
  animateLayout = true,
  children,
  ...props
}: GridLayoutViewComponentProps) => {
  const gapPx = GridLayoutCollision.parsePxLength(gap) ?? 24
  const entries = useMemo(() => extractItems(children), [children])
  const placed = useMemo(
    () => toPlacedItems(entries, columns),
    [entries, columns],
  )
  const nodesById = useMemo(() => {
    const map = new Map<string, ReactNode>()
    for (const entry of entries) map.set(entry.id, entry.node)
    return map
  }, [entries])

  return (
    <GridLayoutCanvas
      items={placed}
      columns={columns}
      gapPx={gapPx}
      animateLayout={animateLayout}
      renderItem={item => nodesById.get(item.id) ?? null}
      {...props}
    />
  )
}
